import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres.");
    if (password !== confirm) return toast.error("As senhas não coincidem.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => navigate("/"), 1800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#eef4ff] to-slate-100 flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-7">
          <div className="flex items-center gap-3 mb-5">
            <img src="/logo-clima-branca-icon.png" alt="Clima Branca" className="h-11 w-11 rounded-xl object-cover" />
            <div className="leading-tight">
              <span className="text-lg font-bold text-slate-800 block">Clima Branca</span>
              <span className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Soluções em climatização</span>
            </div>
          </div>

          {!token ? (
            <div className="text-center py-4" data-testid="reset-invalid">
              <p className="text-slate-600">Link inválido. Solicite uma nova redefinição de senha.</p>
              <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold mt-5 hover:underline">
                Pedir novo link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-800">Senha redefinida!</h1>
              <p className="text-sm text-slate-500 mt-2">Redirecionando para o login...</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-800">Criar nova senha</h1>
              <p className="text-sm text-slate-500 mt-1 mb-5">Escolha uma nova senha para sua conta.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-600">Nova senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type={showPwd ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres" className="pl-9 pr-10 h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="reset-password-input" />
                    <button type="button" onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="reset-toggle-visibility">
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-600">Confirmar senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type={showPwd ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Repita a nova senha" className="pl-9 h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="reset-confirm-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1e7be0] to-[#0f3d92] hover:from-[#1a6fce] hover:to-[#0c3382] text-base font-semibold"
                  data-testid="reset-submit-button">
                  {loading ? "Salvando..." : "Redefinir senha"}
                </Button>
              </form>
              <Link to="/" className="flex items-center justify-center gap-1.5 text-sm text-slate-500 mt-5 hover:text-slate-700">
                <ArrowLeft className="h-4 w-4" /> Voltar ao login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
